import {
  Injectable,
  OnDestroy,
  Optional,
  Inject,
  EventEmitter,
} from "@angular/core";
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse} from "@angular/common/http";
import {
  ServiceRouter,
  IContextService,
  DomainContext,
  AccountContextValue,
  PershingError,
  LogService,
  CoreConstants,
  GroupContextValue,
  IAuthorizationService,
} from "@pershing/pershing-core";
import { BalancesServiceDefinition } from "./balances-service-definition";
import { BehaviorSubject, Observable, Subscription, of } from "rxjs";
import {
  BalancesResponse,
  naBalances,
  Liabilities,
  Disclaimer,
  disclaimerList,
  DisplayParams,
} from "../model/index";
import { BalancesLabels } from "../i18n/balances-labels";
import { BalancesConstants } from "../utils/balances-constants";
import { MortgageServiceDefinition } from "./mortgage-service-definition";
import {
  naTaxReclaim,
  TaxReClaimResponse,
} from "../model/taxreclaim-response.model";
import { QuoteRef, QuoteToggleService } from "@pershing/nxi-ng-quotetoggle";
import { RateBoardServiceDefinition } from "./rateboard-service-definition";

@Injectable()
export class BalancesService implements OnDestroy {
  public helpText: string = "";
  balancesServiceUrl: any;
  displayToggleEmitter = new EventEmitter();
  displayToggle: boolean = true;
  subsVar: Subscription;
  mortgagesServiceUrl: any;
  rateBoardServiceUrl: any;
  balancesServiceError$: BehaviorSubject<any> =
    new BehaviorSubject<PershingError>(null);
  balancesResponse$: BehaviorSubject<any> =
    new BehaviorSubject<BalancesResponse>(null);
  taxreclaimResponse$: BehaviorSubject<any> =
    new BehaviorSubject<TaxReClaimResponse>(null);
  balancesResponseError$: BehaviorSubject<any> =
    new BehaviorSubject<PershingError>(null);
  fetchingBalancesData$: BehaviorSubject<any> = new BehaviorSubject<boolean>(
    null
  );
  displayParams$: DisplayParams = {
    isGroup: false,
    isMixedGroup: false,
    isALLAccount: false,
    isCustodian: false,
    baseCurrency: BalancesConstants.USD_CURRENCY,
  };
  showCurrencyDetails: boolean = false;
  quoteType: string = BalancesConstants.DELAYED;
  realtimeQuote$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  asOfDate: string = new Date().toISOString();
  quoteRef: QuoteRef;

  // Initially no disclaimers should be displayed 'show=false' except default.
  // As component(s) are rendering it'd call updateDisclaimer() which
  // updates the disclaimers list and return the legend value so that both legend and disclaimer symbol
  // are always in synched.
  disclaimersList$: BehaviorSubject<Disclaimer[]> = new BehaviorSubject<
    Disclaimer[]
  >(null);
  disclaimersList: Disclaimer[] = disclaimerList();

  accountNumber: string = BalancesConstants.ACCOUNT_ALL;
  accountName: string;
  contextSubscription: any;
  getBalancesSub: any;
  getTaxReClaimSub: any;
  quoteReferenceSub: any;
  ibd: string;

  balancesUri: string = "/accounts/";
  marginRequired: boolean = true;
  // To ensure currency balances is packed in the response.
  moneyLine: boolean = false;
  groupSummary: boolean = false;
  isBalancesRTQuoteEntitled: boolean;
  constructor(
    private httpClient: HttpClient,
    private serviceRouter: ServiceRouter,
    private contextService: IContextService,
    private quoteToggleService: QuoteToggleService,
    private authorizationService: IAuthorizationService,
    private logService: LogService,
    @Optional() @Inject("AppSettings") appSettings: any
  ) {
    this.ibd = appSettings.ibdConfigMap.IBD;
    this.balancesServiceUrl = this.serviceRouter.getServiceRouteURL(
      BalancesServiceDefinition.getInstance()
    );
    this.mortgagesServiceUrl = this.serviceRouter.getServiceRouteURL(
      MortgageServiceDefinition.getInstance()
    );
    this.rateBoardServiceUrl = this.serviceRouter.getServiceRouteURL(
      RateBoardServiceDefinition.getInstance()
    );
    this.contextSubscription =
      this.contextService.selectedDomainContext$.subscribe(
        (domainContext: DomainContext<any>) => {
          let contextValue = domainContext.value;
          this.resetDisclaimers();
          if (
            contextValue instanceof AccountContextValue &&
            contextValue.accountNumber !== "ALL_ACCOUNTS"
          ) {
            this.accountNumber = contextValue.accountNumber;
            this.balancesUri = "/accounts/";
            this.moneyLine = true;
            this.groupSummary = false;
            this.getBalances(this.accountNumber, this.quoteType);
            this.getTaxReclaimData(this.accountNumber, contextValue);
          } else if (contextValue instanceof GroupContextValue) {
            this.accountNumber = contextValue.groupGUID;
            this.moneyLine = false;
            this.groupSummary = true;
            this.balancesUri = "/groups/";
            this.getBalances(this.accountNumber, this.quoteType);
            this.getTaxReclaimData(this.accountNumber, contextValue);
          } else {
            // // If no account show '-'
            // this.balancesResponse$.next(naBalances());
            // For local dev/demo using mocked data.
            // 1. Comment out above naBalances;
            // 2. Uncomment below and DO NOT check in this change.
            // this.getBalances(this.accountNumber, this.quoteType);
          }
        }
      );
    this.isBalancesRTQuoteEntitled = this.authorizationService.isEntitled('REAL_TIME_EQUITY_QUOTES');
    this.quoteRef = {
      pageName: "balance",
      realTimeRefOn: false,
      asOfDate: this.asOfDate,
      agreementSigned: true,
      quoteAction: "",
      isRTQuoteEntitled: this.isBalancesRTQuoteEntitled,
    };
    this.quoteReferenceSub = this.quoteToggleService.quoteReference$.subscribe(
      (quoteReferenceList) => {
        if (quoteReferenceList) {
          quoteReferenceList.forEach((item) => {
            if (item.pageName === BalancesConstants.PAGENAME) {
              this.quoteType = item.realTimeRefOn
                ? BalancesConstants.REALTIME
                : BalancesConstants.DELAYED;
              this.realtimeQuote$.next(item.realTimeRefOn);
              // Only on quote refresh or toggle.
              if (!!item.quoteAction) {
                this.resetDisclaimers();
                this.getBalances(this.accountNumber, this.quoteType);
              }
            }
          });
        }
      }
    );
  }

  getBalances(accountNumber: string, quoteType: string): any {
    this.fetchingBalancesData$.next(true);
    const url =
      this.balancesServiceUrl +
      this.balancesUri +
      accountNumber +
      "?quoteType=" +
      this.quoteType +
      (this.moneyLine ? "&moneyLine=" + this.moneyLine : "") +
      (this.groupSummary ? "&groupSummary=" + this.groupSummary : "");
    this.getBalancesSub = this.httpClient.get(url).subscribe(
      (response: BalancesResponse) => {
        this.handleBalancesResponse(response);
      },
      (error: HttpErrorResponse) => {
        this.handleError(this.balancesUri, error);
        // Still give balance summary na data and let the component decide to display or not.
        this.balancesResponse$.next(naBalances());
      }
    );
    return this.getBalancesSub;
  }

  getBalanceForOverview(
    accountNumber: string,
    quoteType: string,
    balancesUri: any,
    marginRequired: any,
    moneyLine: any,
    groupSummary: any
  ) {
    this.fetchingBalancesData$.next(true);
    const url =
      this.balancesServiceUrl +
      balancesUri +
      accountNumber +
      "?quoteType=" +
      quoteType +
      (moneyLine ? "&moneyLine=" + moneyLine : "") +
      (groupSummary ? "&groupSummary=" + groupSummary : "");
    return this.httpClient.get(url);
  }

  // For each response - Update following:
  // 1.  View (via DisplayParams Object) - brokerage/custodian/combined.
  // 2.  Quote - asOfDate, Realtime or Delay.
  private processBalancesResponse(rsp: BalancesResponse) {
    let isGroup: boolean =
      rsp &&
      rsp.response.entity &&
      rsp.response.entity.context === "group" &&
      rsp.response.children &&
      rsp.response.children.length > 0;
    this.displayParams$.isGroup = isGroup;
    this.displayParams$.isMixedGroup = false;
    this.displayParams$.isCustodian =
      !!rsp.response.entity.custodianCode &&
      rsp.response.entity.custodianCode ===
        BalancesConstants.BNY_CUSTODIAN_CODE;
    this.displayParams$.baseCurrency = !!rsp.response.entity.baseCurrency
      ? rsp.response.entity.baseCurrency
      : BalancesConstants.USD_CURRENCY;

    this.quoteRef.asOfDate = rsp.response.balances.asOfDate;
    this.quoteRef.realTimeRefOn =
      rsp.response.balances.quoteType === BalancesConstants.REALTIME;
    this.quoteRef.quoteAction = "";
    this.quoteRef.isRTQuoteEntitled = this.isBalancesRTQuoteEntitled;
    this.quoteToggleService.updateQuoteRef(this.quoteRef);

    if (isGroup) {
      this.displayParams$.isGroup = true;

      let bList = rsp.response.children.filter((c: any) => {
        return c.entity.custodianCode !== "BNYM";
      });
      let cList = rsp.response.children.filter((c: any) => {
        return c.entity.custodianCode === "BNYM";
      });
      if (bList.length > 0 && cList.length > 0) {
        // Mixed group.
        this.displayParams$.isMixedGroup = true;
        this.displayParams$.isCustodian = true;
      } else if (bList.length > 0 && cList.length <= 0) {
        // Brokerage Only Group.
        this.displayParams$.isCustodian = false;
      } else if (bList.length <= 0 && cList.length > 0) {
        // Custodian Only Group.
        this.displayParams$.isCustodian = true;
      }
    }
    return rsp;
  }
  private processTaxReClaimResponse(rsp: TaxReClaimResponse) {
    return rsp;
  }

  handleBalancesResponse(response: BalancesResponse) {
    if (response.errorCode === 0) {
      this.balancesResponse$.next(this.processBalancesResponse(response));
      this.clearError(this.balancesResponseError$);
      this.clearError(this.balancesServiceError$);
    } else {
      this.setBalancesResponseError(
        CoreConstants.SYSTEM_ERROR_MESSAGE,
        response.errorCode.toString()
      );
      // Still give balance summary na data and let the component decide to display or not.
      this.balancesResponse$.next(naBalances());
    }
  }

  handleTaxReClaimResponse(response: TaxReClaimResponse) {
    if (response.errorCode === 0) {
      this.taxreclaimResponse$.next(this.processTaxReClaimResponse(response));
      this.clearError(this.balancesResponseError$);
      this.clearError(this.balancesServiceError$);
    }
  }

  // Http failure response e.g. 500, 401, etc.
  private setError(
    reqUri: string,
    errorMessage: string,
    errorCode?: string
  ): void {
    if (!!reqUri && reqUri === this.balancesUri) {
      this.balancesServiceError$.next(
        new PershingError(errorMessage, "BalancesService", errorCode)
      );
    }
  }

  // Balances specific error based on errorCode of BalancesResponse.
  private setBalancesResponseError(
    errorMessage: string,
    errorCode?: string
  ): void {
    this.balancesResponseError$.next(
      new PershingError(errorMessage, "BalancesService", errorCode)
    );
    // Still give balance summary na data and let the component decide to display or not.
    this.balancesResponse$.next(naBalances());
  }

  getLiabilities(): Observable<any> {
    return of({});
  }

  private handleError(reqUri: string, error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // client-side error
      this.logService.error(
        "Error is a client-side error: " + JSON.stringify(error)
      );
      this.setError(
        reqUri,
        CoreConstants.SYSTEM_ERROR_MESSAGE,
        "client-side error"
      );
    } else {
      // server-side error
      if (error.error.errorCode === BalancesConstants.BAD_REQUEST_ERROR) {
        // Balance is not available for more than 45 accounts request.
        this.setError(
          reqUri,
          BalancesLabels.COMMON_ACCOUNT_LIMIT_ERROR,
          error.error.errorCode.toString()
        );
      } else {
        this.logService.error(
          "Error is a server-side error: " + JSON.stringify(error)
        );
        this.setError(
          reqUri,
          CoreConstants.SYSTEM_ERROR_MESSAGE,
          "server-side error"
        );
      }
    }
  }

  clearError(error: any): void {
    error.next(null);
  }

  generateLegendSymbol(dl: Disclaimer[]) {
    let sortedDl = dl;
    dl.reverse();
    dl.sort((a, b) => a.legendSymbol.localeCompare(b.legendSymbol));
    return sortedDl[sortedDl.length - 1].legendSymbol;
  }

  updateDisclaimersList(disclaimer: Disclaimer) {
    // legend by index is dynamic so if disclaimer is configure as such, we need to update its legendSymbol based on last value.
    let lastLegend = this.generateLegendSymbol(
      this.disclaimersList.filter((item) => {
        return item.legendByIndex;
      })
    );
    let reindex: boolean = false;
    for (let rowDisclaimers of this.disclaimersList) {
      if (rowDisclaimers.key === disclaimer.key) {
        if (disclaimer.show && rowDisclaimers.show) {
          // Request to show but already show so just increase the counter;
          rowDisclaimers.showCounter++;
        } else if (!disclaimer.show && rowDisclaimers.show) {
          // Request to remove...but need to also check the counter.
          if (rowDisclaimers.showCounter > 1) {
            rowDisclaimers.showCounter--;
          } else {
            rowDisclaimers.show = false;
            rowDisclaimers.legendSymbol = "0";
            if (rowDisclaimers.legendByIndex) {
              rowDisclaimers.legendSymbol = "0";
            }
            rowDisclaimers.showCounter = 0;
            reindex = true;
          }
        } else if (disclaimer.show) {
          // The very first request to show;
          rowDisclaimers.legendSymbol = rowDisclaimers.legendByIndex
            ? (parseInt(lastLegend, 10) + 1).toString()
            : disclaimer.legendSymbol;
          rowDisclaimers.show = disclaimer.show;
          rowDisclaimers.className = !!disclaimer.className
            ? disclaimer.className
            : "";
          rowDisclaimers.showCounter++;
        }
        break;
      }
    }

    if (reindex) {
      let currentInd: number = 1;
      this.disclaimersList.forEach((item) => {
        if (item.legendByIndex && item.show) {
          item.legendSymbol = currentInd.toString();
          currentInd++;
        }
      });
    }

    this.disclaimersList$.next(this.disclaimersList);
  }

  // 1. Update disclaimer list if needed and 2. return legend symbol so component could display.
  updateDisclaimer(disclaimer: Disclaimer) {
    // Not a known disclaimer so just return empty string.
    // console.log('Requested Disclaimer: ' + disclaimer.show + ' | ' + JSON.stringify(disclaimer));
    if (
      this.disclaimersList.filter((item) => {
        return item.key === disclaimer.key;
      }).length === 0
    ) {
      return "";
    }
    this.updateDisclaimersList(disclaimer);
    return this.disclaimersList.filter((item) => {
      return item.key === disclaimer.key;
    })[0].legendSymbol;
  }

  // Disclaimers are based on data so it should be reset whenever fetching new data.
  // Fetching for new data happens on account changes, quote ref changes, or quote refreshes.
  resetDisclaimers() {
    this.disclaimersList = disclaimerList();
    // Delayed quote disclaimer is set to not shown by default.
    // Explicitly set the disclaimer based on quote state.
    this.toggleDelayQuoteDisclaimer();
  }

  toggleDelayQuoteDisclaimer() {
    let showDelayedDisclaimer = this.quoteType === BalancesConstants.DELAYED;
    this.updateDisclaimer({
      legendSymbol: "\u2020",
      legendByIndex: false,
      key: BalancesLabels.DISCLAIMER_QUOTEDELAYED,
      show: showDelayedDisclaimer,
      className: "quoteDelayed",
      showCounter: 0,
    });
  }

  ngOnDestroy(): void {
    if (this.getBalancesSub) {
      this.getBalancesSub.unsubscribe();
    }
    if (this.contextSubscription) {
      this.contextSubscription.unsubscribe();
    }
    if (this.quoteReferenceSub) {
      this.quoteReferenceSub.unsubscribe();
    }
    if (this.getTaxReClaimSub) {
      this.getTaxReClaimSub.unsubscribe();
    }
  }

  getForeignExchangeData(): Observable<any> {
    return this.httpClient.get(this.rateBoardServiceUrl + "/fxRate");
  }

  getTaxReclaimData(accountNumber: string, contextValue): any {
    this.fetchingBalancesData$.next(true);
    let url;
    if (contextValue.hasOwnProperty('groupGUID')) {
      url = this.balancesServiceUrl + "/groupTaxReclaim/" + accountNumber;
    } else {
      url = this.balancesServiceUrl + "/accountTaxReclaim/" + accountNumber;
    }
    
    this.getTaxReClaimSub = this.httpClient.get(url).subscribe(
      (response: TaxReClaimResponse) => {
        this.handleTaxReClaimResponse(response);
      },
      (error: HttpErrorResponse) => {
        this.handleError(this.balancesUri, error);
        this.fetchingBalancesData$.next(false);
        // Still give balance summary na data and let the component decide to display or not.
        this.taxreclaimResponse$.next(naTaxReclaim());
      }
    );
    return this.getTaxReClaimSub;
  }

  setShowCurrencyDetails(value) {
    this.showCurrencyDetails = value;
  }
  getShowCurrencyDetails() {
    return this.showCurrencyDetails;
  }
  getIfAllAccount() {
    return this.accountNumber.indexOf("ALL_ACCOUNTS") !== -1;
  }
  getAccountContext() {
    return this.accountNumber;
  }
  setDisplayToggle(value) {
    this.displayToggle = value;
    this.displayToggleEmitter.emit();
  }
  getDisplayToggle() {
    return this.displayToggle;
  }

  dispatchOverviewBalance(res: any) {
    if(res !== null && res !== 'undefined'){
      document.dispatchEvent(new CustomEvent("balanceDataLoaded", {detail: res}));
    }
  }
}
